import { anonId, clearAccountUid, deviceAnonId, setAccountUid } from "./engagement";
import { getIdToken } from "./auth";
import { editionMark, editionScore, pickEdition } from "./v3/editionRank";
import type { InSnipCard } from "./types";

/**
 * Client for the per-user session API (Cloudflare Worker + D1). When
 * `VITE_API_URL` is set the app serves rounds from the store (server-side
 * seen-log, resume, hard-refresh, freshness) instead of the bundled cards.json.
 * Every call is best-effort: a network failure returns null so the app can fall
 * back to the static feed and never breaks.
 */

const API = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

export function sessionApiEnabled(): boolean {
  return API.length > 0;
}

export type SessionRound = {
  sessionId: string;
  position: number;
  roundSize: number;
  cards: InSnipCard[];
  hasMore: boolean;
};

async function getJson<T>(
  path: string,
  timeoutMs = 4000,
  headers?: Record<string, string>,
): Promise<T | null> {
  if (!API) return null;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(`${API}${path}`, { signal: ctrl.signal, headers });
    clearTimeout(t);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function postJson<T>(
  path: string,
  body: unknown,
  timeoutMs = 4000,
  extraHeaders?: Record<string, string>,
): Promise<T | null> {
  if (!API) return null;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...extraHeaders },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    clearTimeout(t);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/** Topic preferences the reader set in "Make It Yours": followed categories are
 *  weighted up; muted categories are hidden (with a server-side diversity floor). */
export type TopicPrefs = { preferred?: string[]; muted?: string[] };

/** Feed ordering for THIS session only (not persisted): "latest" = newest-first recency,
 *  "trending" = most popular first. Switching it rebuilds the deck. */
export type FeedMode = "latest" | "trending";

/** Resume the open round or build the next 10 for this reader. */
export function fetchSession(
  preferred: string[] = [],
  prefs: TopicPrefs = {},
  mode: FeedMode = "latest",
): Promise<SessionRound | null> {
  const params = new URLSearchParams({ u: anonId(), lean: "1" });
  const followed = prefs.preferred ?? preferred;
  if (followed.length) params.set("preferred", followed.join(","));
  if (prefs.muted && prefs.muted.length) params.set("muted", prefs.muted.join(","));
  if (mode === "trending") params.set("mode", "trending");
  return getJson<SessionRound>(`/session?${params.toString()}`);
}

/** Complete the current round (marks it seen) and fetch the next 10. */
export function nextSession(
  preferred: string[] = [],
  prefs: TopicPrefs = {},
  mode: FeedMode = "latest",
): Promise<SessionRound | null> {
  return postJson<SessionRound>("/next?lean=1", {
    u: anonId(),
    preferred: prefs.preferred ?? preferred,
    muted: prefs.muted ?? [],
    mode,
  });
}

/** Refresh, bring the freshest cards to the top. `viewed` = the cards the reader actually
 *  saw this round; only those are consumed (marked seen), so unread cards stay queued. */
export function refreshSession(
  preferred: string[] = [],
  prefs: TopicPrefs = {},
  viewed: string[] = [],
  mode: FeedMode = "latest",
): Promise<SessionRound | null> {
  return postJson<SessionRound>("/refresh?lean=1", {
    u: anonId(),
    preferred: prefs.preferred ?? preferred,
    muted: prefs.muted ?? [],
    viewed: viewed.slice(0, 50),
    mode,
  });
}

/** Remember the exact card the reader is on (best-effort) for cross-device resume. */
export function updatePosition(position: number): Promise<unknown> {
  return postJson("/position", { u: anonId(), position });
}

/** Mark cards as seen (the permanent never-repeat log). */
export function markSeen(cardIds: string[], reaction?: string): Promise<unknown> {
  if (cardIds.length === 0) return Promise.resolve(null);
  return postJson("/seen", { u: anonId(), cardIds, reaction });
}

/** Fetch one card by id, for a shared deep link, so the recipient lands on the
 *  exact shared story even if it isn't in their own round. */
export async function fetchCard(id: string): Promise<InSnipCard | null> {
  const data = await getJson<{ card: InSnipCard }>(`/card?id=${encodeURIComponent(id)}`);
  return data?.card ?? null;
}

/** The daily Brief digests (newest first), its own per-day feature, not in the feed. */
export async function fetchBriefs(limit = 14): Promise<InSnipCard[]> {
  const data = await getJson<{ briefs: InSnipCard[] }>(`/briefs?limit=${limit}`);
  return data?.briefs ?? [];
}

/** Today's daily news quiz (its questions live in the card payload), or null. */
export async function fetchQuiz(): Promise<InSnipCard | null> {
  const data = await getJson<{ quiz: InSnipCard | null }>(`/quiz`);
  return data?.quiz ?? null;
}

/** One "on this day in history" entry (or famous person born that day). */
export interface OnThisDayItem { year: number; text: string; page: string | null }

/** Historical events + famous births for a calendar day (month, day), for the birthday panel.
 *  Best effort: empty lists on failure so the panel still shows the locally computed insights. */
export async function fetchOnThisDay(
  month: number,
  day: number,
): Promise<{ events: OnThisDayItem[]; births: OnThisDayItem[] }> {
  const data = await getJson<{ events: OnThisDayItem[]; births: OnThisDayItem[] }>(
    `/onthisday?m=${month}&d=${day}`,
  );
  return { events: data?.events ?? [], births: data?.births ?? [] };
}

/** "More like this": the most semantically similar unseen cards to `id` (empty on failure). */
export async function fetchRelated(id: string): Promise<InSnipCard[]> {
  if (!id) return [];
  const data = await getJson<{ related: InSnipCard[] }>(
    `/related?id=${encodeURIComponent(id)}&u=${encodeURIComponent(anonId())}&lean=1`,
  );
  return data?.related ?? [];
}

/** The Good News shelf, straight from the server.
 *
 *  `cards` are the ones jobs/good_news.py CONFIRMED by reading them, newest first, over a
 *  rolling window (`days`); `today` is how many of those carry today's Sydney date. The two
 *  numbers differ most of the morning, because the judge first runs at 14:10, and the caller
 *  needs to know which one it is describing.
 *
 *  Best-effort like every other call here: on failure the panel keeps whatever the deck has
 *  already contributed rather than emptying itself. */
export type GoodNewsShelf = { cards: InSnipCard[]; today: number; days: number };
export async function fetchGoodNews(): Promise<GoodNewsShelf | null> {
  const data = await getJson<GoodNewsShelf>(`/goodnews?lean=1`);
  if (!data || !Array.isArray(data.cards)) return null;
  return { cards: data.cards, today: Number(data.today ?? 0), days: Number(data.days ?? 0) };
}

/** A story a reader has to act on, from the worker's /heads-up (worker/src/headsup.ts). */
export type HeadsUp = {
  key: string; cardId: string; kind: string; title: string; do: string; who: string; states: string[];
  createdAt: number; updatedAt: number; expiresAt: number; image: string | null; source: string | null; url?: string | null;
};
export async function fetchHeadsUp(): Promise<HeadsUp[]> {
  // A preview reads its alerts from the staging worker while its cards stay real; production never sets this.
  const elsewhere = import.meta.env.VITE_HEADS_UP_URL as string | undefined;
  const data = elsewhere
    ? await fetch(`${elsewhere}/heads-up`).then((r) => r.json() as Promise<{ alerts: HeadsUp[] }>).catch(() => null)
    : await getJson<{ alerts: HeadsUp[] }>("/heads-up");
  return Array.isArray(data?.alerts) ? data.alerts : [];
}

/** Semantic + keyword search over the store (returns seen cards too, never empty).
 * `weak` = nothing matched well, so these are "closest matches" rather than hits. */
export type SearchResult = { cards: InSnipCard[]; weak: boolean; semantic: boolean; limited?: boolean };
// Story search is the one read the worker limits per minute, and the home's story rows, every
// tool's "in the news" and the jump box all ask it. An answer is kept for ten minutes, one call
// per query is in flight at a time, and a 429 is remembered for its Retry-After, so a busy
// minute of tabs never turns into a minute of empty rows. `limited` tells a caller the empty
// answer is the limit speaking, not the store.
const SEARCH_TTL_MS = 10 * 60_000;
const searchMemo = new Map<string, { at: number; value: SearchResult }>();
const searchFlight = new Map<string, Promise<SearchResult>>();
let searchBlockedUntil = 0;
const LIMITED: SearchResult = { cards: [], weak: true, semantic: false, limited: true };
export async function searchCards(query: string): Promise<SearchResult> {
  const q = query.trim();
  const hit = searchMemo.get(q);
  if (hit && Date.now() - hit.at < SEARCH_TTL_MS) return hit.value;
  if (Date.now() < searchBlockedUntil) return LIMITED;
  const inFlight = searchFlight.get(q);
  if (inFlight) return inFlight;
  const call = (async (): Promise<SearchResult> => {
    if (!API) return { cards: [], weak: false, semantic: false };
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(`${API}/search?u=${encodeURIComponent(anonId())}&q=${encodeURIComponent(q)}`, { signal: ctrl.signal });
      clearTimeout(t);
      if (res.status === 429) {
        const wait = Number(res.headers.get("Retry-After")) || 60;
        searchBlockedUntil = Date.now() + Math.min(Math.max(wait, 5), 120) * 1000;
        return LIMITED;
      }
      if (!res.ok) return LIMITED;
      const data = (await res.json()) as { cards?: InSnipCard[]; weak?: boolean; semantic?: boolean };
      const value: SearchResult = { cards: data?.cards ?? [], weak: Boolean(data?.weak), semantic: Boolean(data?.semantic) };
      searchMemo.set(q, { at: Date.now(), value });
      return value;
    } catch {
      return LIMITED;
    } finally {
      searchFlight.delete(q);
    }
  })();
  searchFlight.set(q, call);
  return call;
}

export interface AccountSummary {
  signedIn: boolean;
  email?: string | null;
  name?: string | null;
  memberSince?: number;
  streak?: number;
  longestStreak?: number;
  cardsRead?: number;
}

/** Sign in: send the verified Firebase ID token + this device's anon id to the worker,
 * which adopts the Firebase uid as the cross-device account id and merges the device's
 * seen-log + streak into it. On success the account uid becomes the active `u`. */
export async function linkAccount(
  idToken: string,
): Promise<{ uid: string; email: string | null; name: string | null } | null> {
  const data = await postJson<{ uid: string; email: string | null; name: string | null }>(
    "/account/link",
    { idToken, u: deviceAnonId() },
    9000,
  );
  if (data?.uid) {
    setAccountUid(data.uid);
    return data;
  }
  return null;
}

/** Drop the account identity locally (server data is untouched; the device reverts to
 * its anonymous id). Pair with auth.signOutUser(). */
export function unlinkAccount(): void {
  clearAccountUid();
}

/** Signed-in profile summary for the current active user (email, streak, reads).
 *  The uid alone is not proof of identity, so the worker only returns the EMAIL to a caller
 *  carrying that account's Firebase ID token. Everything else comes back either way, so a
 *  reader with no live Firebase session still sees their name, streak and read count. */
export async function fetchAccount(): Promise<AccountSummary> {
  let auth: Record<string, string> | undefined;
  try {
    const token = await getIdToken();
    if (token) auth = { Authorization: `Bearer ${token}` };
  } catch {
    // No Firebase session (or it failed to refresh): fall through unauthenticated.
  }
  const data = await getJson<AccountSummary>(`/account?u=${encodeURIComponent(anonId())}`, 4000, auth);
  return data ?? { signedIn: false };
}

/** How many friends a user has brought in (the invite mechanic). */
export async function fetchReferrals(u: string): Promise<number> {
  const data = await getJson<{ count: number }>(`/referrals?u=${encodeURIComponent(u)}`);
  return data?.count ?? 0;
}

/** Save the reader's profile (guest or full) + interests. The worker writes the email, the
 *  mode, and the name of an account that has an email only for a caller carrying that
 *  account's Firebase ID token, so a signed-in reader sends it; signed out, the rest still saves. */
export async function saveProfile(profile: {
  mode: "guest" | "profile";
  firstName?: string;
  lastName?: string;
  ageBand?: string;
  email?: string;
  interests?: string[];
}): Promise<unknown> {
  let auth: Record<string, string> | undefined;
  try {
    const token = await getIdToken();
    if (token) auth = { Authorization: `Bearer ${token}` };
  } catch {
    // No Firebase session: save unauthenticated.
  }
  return postJson("/user", { u: anonId(), ...profile }, 4000, auth);
}

export type Insights = { cardsRead: number; topCategory: string | null; freshPool: number };
export function fetchInsights(): Promise<Insights | null> {
  return getJson<Insights>(`/insights?u=${encodeURIComponent(anonId())}`);
}

/** Count of newly-released unseen feed cards since `after` (epoch seconds), powers the
 *  "N new snips available" pill. Returns 0 on any failure (the pill just stays hidden). */
export async function fetchFreshCount(after: number): Promise<number> {
  if (!after) return 0;
  const data = await getJson<{ count: number }>(
    `/fresh?u=${encodeURIComponent(anonId())}&after=${Math.floor(after)}`,
  );
  return data?.count ?? 0;
}

/** An Ask answer plus the sources it was grounded on (empty on workers that predate them). */
export type AskAnswer = { answer: string; sources: { name: string; url: string }[] };

/**
 * Ask the LLM a follow-up question about a card, grounded in that card.
 * Unlike the best-effort helpers above, this surfaces failures (it powers an
 * interactive Q/A panel that needs to show a real answer or an error), so it
 * resolves with the answer (and any named sources) or throws.
 */
export async function askCard(cardId: string, question: string): Promise<AskAnswer> {
  if (!API) throw new Error("ask unavailable");
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 15000);
  try {
    // A signed-in reader proves the account uid with their Firebase ID token; signed out,
    // or if the refresh fails, the request goes without it exactly as before.
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    try {
      const token = await getIdToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* no Firebase session: ask unauthenticated */
    }
    const res = await fetch(`${API}/ask`, {
      method: "POST",
      headers,
      body: JSON.stringify({ u: anonId(), cardId, question: question.slice(0, 300) }),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error((detail as { error?: string }).error ?? `ask failed (${res.status})`);
    }
    const data = (await res.json()) as { answer?: string; sources?: unknown };
    const answer = (data.answer ?? "").trim();
    if (!answer) throw new Error("empty answer");
    // Sources are new on the worker side, so decode defensively: absent (an old worker)
    // or malformed both mean "no citations", never a failed answer.
    const sources = Array.isArray(data.sources)
      ? data.sources.flatMap((s) => {
          const rec = s as { name?: unknown; url?: unknown } | null;
          return rec && typeof rec.name === "string" && rec.name
            ? [{ name: rec.name, url: typeof rec.url === "string" ? rec.url : "" }]
            : [];
        })
      : [];
    return { answer, sources };
  } finally {
    clearTimeout(timer);
  }
}

/** The newest public stories (headline + masthead name), slim. Powers the sign-in wall's
 *  frosted tease; [] on any failure so the wall simply shows no tease. */
/** Alerts on numbers (worker /alerts): the reader asks to hear when a figure moves. */
export type AlertKind = "fuel_under" | "rba_decision" | "rent_change";
export type Alert = { id: string; kind: AlertKind; params: Record<string, unknown>; created_at: number; last_fired_at: number | null };
async function authHeader(): Promise<Record<string, string> | undefined> {
  try {
    const token = await getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  } catch {
    return undefined;
  }
}
export async function createAlert(kind: AlertKind, params: Record<string, unknown>): Promise<{ id: string } | { error: string } | null> {
  if (!API) return null;
  const auth = await authHeader();
  try {
    const res = await fetch(`${API}/alerts`, { method: "POST", headers: { "Content-Type": "application/json", ...auth }, body: JSON.stringify({ u: anonId(), kind, params }) });
    const data = (await res.json().catch(() => null)) as { id?: string; error?: string } | null;
    if (res.ok && data?.id) return { id: data.id };
    return { error: data?.error || `http_${res.status}` };
  } catch {
    return null;
  }
}
export async function listAlerts(): Promise<Alert[]> {
  const data = await getJson<{ alerts: Alert[] }>(`/alerts?u=${encodeURIComponent(anonId())}`, 4000, await authHeader());
  return Array.isArray(data?.alerts) ? data.alerts : [];
}
export async function deleteAlert(id: string): Promise<boolean> {
  if (!API) return false;
  try {
    const res = await fetch(`${API}/alerts?id=${encodeURIComponent(id)}&u=${encodeURIComponent(anonId())}`, { method: "DELETE", headers: { ...(await authHeader()) } });
    // An alert the store no longer has (expired, or removed elsewhere) is off: the button must
    // not stay lit for an id that can never be deleted.
    return res.ok || res.status === 404;
  } catch {
    return false;
  }
}
/** Mirror the saved place to the store, so alerts and the weekly email can speak for it. */
export async function savePlace(place: { name: string; state: string; postcode?: string; lat: number; lng: number }): Promise<void> {
  if (!API) return;
  // A store without the route answers 404; remember that for the session rather than asking again.
  const MISSING = "insnip.placeRouteMissing";
  try { if (sessionStorage.getItem(MISSING)) return; } catch { /* fine */ }
  try {
    const res = await fetch(`${API}/place`, { method: "POST", headers: { "Content-Type": "application/json", ...(await authHeader()) }, body: JSON.stringify({ u: anonId(), ...place }) });
    if (res.status === 404) { try { sessionStorage.setItem(MISSING, "1"); } catch { /* fine */ } }
  } catch {
    /* best effort */
  }
}

export type LatestTeaser = { id: string; headline: string; source: string; category?: string; imageUrl?: string; at?: number };
export async function fetchLatestTeasers(limit = 3): Promise<LatestTeaser[]> {
  const data = await getJson<{ cards: LatestTeaser[] }>(`/latest?limit=${limit}`);
  return Array.isArray(data?.cards) ? data.cards : [];
}

/** The same story across days (worker /story-chain; payload.storyId is the key). A chain
 *  of 0 or 1 means no movement to show, and the UI renders nothing. */
export type StoryChainStep = { id: string; headline: string; source: string; at: number };
export async function fetchStoryChain(id: string): Promise<StoryChainStep[]> {
  if (!id) return [];
  const data = await getJson<{ chain: StoryChainStep[] }>(`/story-chain?id=${encodeURIComponent(id)}`);
  return Array.isArray(data?.chain) ? data.chain : [];
}

/** Mint a public day-share code (worker POST /day) for insnip.com/d/<code>. Numbers only;
 *  null on any failure so Share my day falls back to the image share alone. */
export async function mintDayShare(body: {
  day: string;
  cards: number;
  sources: number;
  streak: number;
  fields: number;
  name?: string;
}): Promise<string | null> {
  try {
    const res = (await postJson("/day", body)) as { ok?: boolean; code?: string } | null;
    return res && res.ok && typeof res.code === "string" ? res.code : null;
  } catch {
    return null;
  }
}

/** The most read stories right now across readers, as ids, most read first. */
export async function fetchTrendingIds(limit = 6): Promise<string[]> {
  const data = await getJson<{ cards?: unknown[] }>(`/trending?limit=${limit}`);
  return Array.isArray(data?.cards) ? data.cards.filter((x): x is string => typeof x === "string") : [];
}

/** A story's place on the front page and what the page may say about it. */
export type EditionMark = { rank: number; readers?: number; hot?: boolean; justIn?: boolean; breaking?: boolean };
/** A line on the wire: the newest stories, on the page or not. */
export type EditionWire = { id: string; headline: string; at: number; category?: string };
/** The front page for this hour: the best of the last `hours` hours, set at `at` (epoch seconds).
 *  `hot` is the most-read order when enough readers have been through, else empty. */
export type EditionPaper = { at: number; hours: number; cards: InSnipCard[]; hot: string[]; marks: Record<string, EditionMark>; wire: EditionWire[]; composed?: boolean };

/** The front page (worker GET /edition). A worker that does not serve it yet gets the page set
 *  by hand from the public reads, so the page never depends on a deploy order. */
export async function fetchEdition(limit = 12): Promise<EditionPaper | null> {
  const data = await getJson<{ at?: number; hours?: number; cards?: InSnipCard[]; hot?: unknown[]; marks?: Record<string, EditionMark>; wire?: EditionWire[] }>(
    `/edition?limit=${limit}&lean=1`,
    8000,
  );
  if (data && Array.isArray(data.cards) && data.cards.length) {
    return {
      at: Number(data.at) || Math.floor(Date.now() / 1000),
      hours: Number(data.hours) || 24,
      cards: data.cards,
      hot: Array.isArray(data.hot) ? data.hot.filter((x): x is string => typeof x === "string") : [],
      marks: data.marks && typeof data.marks === "object" ? data.marks : {},
      wire: Array.isArray(data.wire) ? data.wire.filter((w) => w && typeof w.id === "string" && typeof w.headline === "string") : [],
    };
  }
  return composeEdition(limit);
}

/** The same page set by hand, ranked exactly as the worker ranks it (v3/editionRank.ts): the
 *  newest stories of the last 24 hours are read in full (the public card read is edge-cached),
 *  scored, capped by subject, led by a picture. Heavier than one call to /edition, which is
 *  why that route exists. */
async function composeEdition(limit: number): Promise<EditionPaper | null> {
  const [teasers, hot] = await Promise.all([fetchLatestTeasers(60), fetchTrendingIds(10)]);
  if (!teasers.length) return null;
  const now = Math.floor(Date.now() / 1000);
  let pool = teasers.filter((t) => (t.at ?? 0) >= now - 24 * 3600);
  if (pool.length < 12) pool = teasers.slice(0, Math.max(12, pool.length));
  pool = pool.slice(0, 40);
  const full = (await Promise.all(pool.map((t) => fetchCard(t.id).catch(() => null)))).filter((c): c is InSnipCard => !!c);
  if (!full.length) return null;
  const hotRank = new Map(hot.map((id, i) => [id, i] as const));
  type Extra = { trendingScore?: number; breakingScore?: number; region?: string; discussion?: { buzz?: number } };
  const scored = full.map((c) => {
    const x = c as InSnipCard & Extra;
    const t = Date.parse(c.publishedAt);
    const ageH = Number.isFinite(t) ? Math.max(0, (Date.now() - t) / 3600000) : 24;
    const h = hotRank.get(c.id) ?? -1;
    const breaking = Number(x.breakingScore) || 0;
    const category = typeof c.category === "string" ? c.category : "";
    // Without reader counts, a place in the most-read list stands in for them.
    const readers = h >= 0 ? Math.max(2, 10 - h) : 0;
    const score = editionScore({
      trending: Number(x.trendingScore) || 0, breaking, buzz: Number(x.discussion?.buzz) || 1, ageH,
      readers, weight: readers * 3, sources: 1 + (c.supportingSources?.length ?? 0), insight: (c.insightScore ?? 0) > 0,
      au: String(x.region ?? "").toUpperCase() === "AU", picture: !!c.imageUrl, category,
    });
    return { c, score, ageH, breaking, h, readers, category, picture: !!c.imageUrl };
  });
  const picked = pickEdition(scored, limit);
  const marks: Record<string, EditionMark> = {};
  picked.forEach((s, i) => {
    marks[s.c.id] = { rank: i + 1, ...editionMark({ breaking: s.breaking, ageH: s.ageH, hotRank: s.h, readers: s.readers }) };
  });
  const cards = picked.map((s) => s.c);
  const hotIn = hot.filter((id) => cards.some((c) => c.id === id));
  const wire: EditionWire[] = [...teasers]
    .filter((t) => t.id && t.headline && t.at)
    .sort((a, b) => (b.at ?? 0) - (a.at ?? 0))
    .slice(0, 8)
    .map((t) => ({ id: t.id, headline: t.headline, at: t.at ?? now, category: t.category }));
  return { at: now, hours: 24, cards, hot: hotIn.length >= 3 ? hotIn : [], marks, wire, composed: true };
}
